import { getIsUserAuthenticatedFromState } from "@/redux/auth.reducer"
import type { RootState } from "@/redux/root.reducer"
import { getIsAdminModeEnabledFromState } from "@/redux/selectors"
import { useRootSelector } from "@/redux/useRootSelector"
import { getIsMultifarmUserFromState } from "@/redux/user-farms.selectors"
import type { AcceptsChildren } from "@/theme"
import { Platform } from "react-native"

interface FilterOptions {
  displayName?: string
  fallbackElement?: React.JSX.Element
}
type Props = AcceptsChildren & FilterOptions
/**
 * If the filter return true, show the component. Otherwise, show the fallback
 */
function makeFilterComponent({
  displayName,
  getIsVisible,
  ...defaultOptions
}: FilterOptions & {
  getIsVisible: (state: RootState) => boolean
}): React.FC<Props> {
  function Filter({ children, fallbackElement }: Props) {
    const isVisible = useRootSelector(getIsVisible)
    if (isVisible) {
      return children as React.JSX.Element | null
    }
    return fallbackElement ?? defaultOptions.fallbackElement ?? null
  }
  if (typeof displayName === "string") {
    Filter.displayName = displayName
  }
  return Filter
}

/**
 * Shown only in development mode
 */
export const DevOnly = makeFilterComponent({
  displayName: "DevOnly",
  getIsVisible: () => __DEV__,
})

/**
 * Show only if admin
 */
export const AdminOnly = makeFilterComponent({
  displayName: "AdminOnly",
  getIsVisible: (state) => __DEV__ || getIsAdminModeEnabledFromState(state),
})

export const AuthenticatedOnly = makeFilterComponent({
  displayName: "AuthenticatedOnly",
  getIsVisible: (state) => getIsUserAuthenticatedFromState(state),
})

export const MultifarmOnly = makeFilterComponent({
  displayName: "MultifarmOnly",
  getIsVisible: (state) => getIsMultifarmUserFromState(state),
})
export const AndroidOnly = makeFilterComponent({
  displayName: "AndroidOnly",
  getIsVisible: () => Platform.OS === "android",
})
